import type { TRPCLink } from '@trpc/client'
import { observable } from '@trpc/server/observable'
import type { AnyRouter } from '@trpc/server'
import { getRouteTel, type Tel } from '@w/tel'
import { ulid } from '@w/utils'
import { BaseDomainError } from '@w/tel'
import { platformI18tArgs } from '@w/fe-i18n-platform'

type PlatformDomainErrorType = 'AUTHORIZATION_ERROR'

export class AuthorizationError extends BaseDomainError<PlatformDomainErrorType> {
  constructor(userMessage: unknown[], message?: string) {
    super('AUTHORIZATION_ERROR', userMessage, { message, shouldContactSupport: false })
  }
}

/**
 * tRPC link that:
 * 1. Removes the `span` field from `input.tel`
 * 2. Creates a span (using getRouteTel())
 * 3. Ends the span on the first `next` (or on error)
 * 4. Injects `tel` with traceId/spanId when missing
 * 5. For File/Blob uploads, stores telemetry in operation context for header injection
 */
export const telLink: TRPCLink<AnyRouter> = () => {
  return ({ next, op }) => {
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    const input = op.input as any
    const inputTel = input?.tel

    const parentTel: Tel = input?.tel || getRouteTel()

    const id = ulid()
    const span = parentTel._createSpan({
      id,
      requestId: id,
      name: op.path,
      type: op.type,
      input: op.input,
      target: 'fetch',
    })

    const baggage = span._getBaggage()

    // 2) Remove `tel.span` or inject `tel` when missing
    let processedOp = { ...op }
    
    // Handle File/Blob objects specially - preserve them without tel injection
    // Store baggage in operation context for header injection
    if (input instanceof File || input instanceof Blob) {
      processedOp = { ...op, input, context: { ...op.context, telBaggage: baggage } }
    } else if (input && typeof input === 'object') {
      const newInput = { ...input }

      if (inputTel && typeof inputTel === 'object') {
        newInput.tel = baggage
      } else newInput.tel = baggage

      processedOp = { ...op, input: newInput }
    } else if (input === undefined) {
      processedOp = { ...op, input: { tel: baggage } }
    } else {
      // For primitives (strings, numbers, etc.) we cannot inject tel
      processedOp = { ...op, input }
    }

    // if (globalThis.TRACE_ERROR && globalThis.EXPORT_TRACES) {
    // console.info('Making a trpc request from the client', processedOp) // uncomment if you are unsure that requests are sent to the backend
    // }

    return observable((observer) => {
      const sub = next(processedOp).subscribe({
        next(value) {
          span.end({ output: value })
          observer.next(value)
        },
        error(error) {
          error.message = `${op.path} ${error.message}`

          // Map authorization-related TRPC errors (401/403) into a Domain AuthorizationError
          const data = (error as { data?: { httpStatus?: number; code?: string } }).data
          const status = data?.httpStatus

          if (status === 401 || status === 403) {
            const authorizationError = new AuthorizationError(
              status === 401
                ? platformI18tArgs('platform:error.auth.unauthorized')
                : platformI18tArgs('platform:error.auth.forbidden'),
              error.message,
            )
            // biome-ignore lint/suspicious/noExplicitAny: <explanation>
            ;(error as any).cause = authorizationError

            if (op.path.startsWith('tel.')) span.end({ error: error }, { errorLevel: 'warning' })
            else span.end({ error: error })
            observer.error(error)
            return
          }

          if (op.path.startsWith('tel.')) span.end({ error }, { errorLevel: 'warning' })
          else span.end({ error })
          observer.error(error)
        },
        complete() {
          observer.complete()
        },
      })

      return () => sub.unsubscribe()
    })
  }
}
